import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Save, Mail, Phone, TrendingUp, Users2, Crown } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const ROLE_LABELS = { admin: "Admin", seller: "Venditore", outreach: "Outreach", artist: "Artista" };
const ROLE_ORDER = ["seller", "outreach", "artist", "admin"];
const EMPTY = { email: "", password: "", name: "", role: "seller", phone: "", commission_pct: 15, parent_seller_id: "", override_pct: 0, active: true, linked_artist_id: "" };

export default function CRMUsers() {
  const [users, setUsers] = useState([]);
  const [artists, setArtists] = useState([]);
  const [tab, setTab] = useState("seller");
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => Promise.all([
    api.get("/crm/users"),
    api.get("/artists"),
  ]).then(([u, a]) => { setUsers(u.data); setArtists(a.data); setLoading(false); });
  useEffect(() => { load(); }, []);

  const filtered = users.filter((u) => u.role === tab);
  const sellers = users.filter((u) => u.role === "seller");
  const parentName = (id) => sellers.find((s) => s.id === id)?.name || sellers.find((s) => s.id === id)?.email || null;
  const artistName = (id) => artists.find((a) => a.id === id)?.name || null;
  const countByRole = (r) => users.filter((u) => u.role === r).length;

  const save = async () => {
    try {
      if (editing.id) {
        const payload = {
          name: editing.name,
          phone: editing.phone,
          role: editing.role,
          commission_pct: parseFloat(editing.commission_pct) || 0,
          parent_seller_id: editing.parent_seller_id || null,
          override_pct: parseFloat(editing.override_pct) || 0,
          active: editing.active,
          linked_artist_id: editing.linked_artist_id || null,
        };
        await api.put(`/crm/users/${editing.id}`, payload);
        toast.success("Utente aggiornato");
      } else {
        await api.post("/crm/users", {
          ...editing,
          commission_pct: parseFloat(editing.commission_pct) || 0,
          override_pct: parseFloat(editing.override_pct) || 0,
          parent_seller_id: editing.parent_seller_id || null,
          linked_artist_id: editing.linked_artist_id || null,
        });
        toast.success("Utente creato");
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Confermi l'eliminazione?")) return;
    try {
      await api.delete(`/crm/users/${id}`);
      toast.success("Eliminato");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="p-6 lg:p-10 max-w-6xl" data-testid="crm-users-page">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-[#FF5A1F]">— Team CRM</div>
          <h1 className="font-display text-5xl uppercase text-[#0A0A0A] mt-2">Utenti</h1>
          <p className="text-[#0A0A0A]/60 mt-2 max-w-2xl">Venditori, outreach, artisti e admin. Le commissioni si applicano solo ai venditori. Un venditore può avere un referente senior (override).</p>
        </div>
        <button onClick={() => setEditing({ ...EMPTY, role: tab })} className="btn-cta" data-testid="crm-user-add">
          <Plus size={16} /> Aggiungi {ROLE_LABELS[tab]}
        </button>
      </div>

      {/* Tabs */}
      <div className="mb-8 flex gap-1 flex-wrap border-b-2 border-[#0A0A0A]">
        {ROLE_ORDER.map((r) => (
          <button key={r} onClick={() => setTab(r)} data-testid={`crm-users-tab-${r}`}
            className={`px-5 py-3 -mb-[2px] border-b-4 text-sm font-bold uppercase tracking-widest transition-colors ${tab === r ? "border-[#FF5A1F] text-[#0A0A0A]" : "border-transparent text-[#0A0A0A]/50 hover:text-[#0A0A0A]/80"}`}>
            {ROLE_LABELS[r]}
            <span className={`ml-2 text-[10px] px-1.5 py-0.5 ${tab === r ? "bg-[#FF5A1F] text-[#FAFAF7]" : "bg-[#0A0A0A]/10"}`}>{countByRole(r)}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-[#0A0A0A]/50">Caricamento…</div>
      ) : filtered.length === 0 ? (
        <div className="border-2 border-dashed border-[#0A0A0A]/30 p-12 text-center text-[#0A0A0A]/50" data-testid="crm-users-empty">
          Nessun {ROLE_LABELS[tab].toLowerCase()} ancora. Clicca su "Aggiungi {ROLE_LABELS[tab]}" per crearne uno.
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {filtered.map((u) => (
            <UserCard
              key={u.id}
              user={u}
              parentName={parentName(u.parent_seller_id)}
              linkedArtistName={artistName(u.linked_artist_id)}
              onEdit={() => setEditing(u)}
              onRemove={() => remove(u.id)}
            />
          ))}
        </div>
      )}

      {editing && (
        <EditUserModal
          data={editing}
          sellers={sellers}
          artists={artists}
          onChange={setEditing}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}
    </div>
  );
}

const UserCard = ({ user, parentName, linkedArtistName, onEdit, onRemove }) => {
  const isSeller = user.role === "seller";
  const isArtist = user.role === "artist";
  return (
    <div className="border-2 border-[#0A0A0A] bg-[#FAFAF7] p-5 flex flex-col gap-4" data-testid={`user-row-${user.id}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="shrink-0 w-11 h-11 border-2 border-[#0A0A0A] bg-[#FF5A1F] text-[#FAFAF7] font-display text-lg uppercase flex items-center justify-center">
            {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0">
            <h3 className="font-bold text-lg text-[#0A0A0A] truncate">{user.name || "—"}</h3>
            <div className="flex items-center gap-2 flex-wrap mt-0.5">
              <span className="text-[10px] uppercase tracking-widest bg-[#0A0A0A] text-[#FAFAF7] px-1.5 py-0.5 font-bold">{ROLE_LABELS[user.role]}</span>
              {user.active !== false ? (
                <span className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">● Attivo</span>
              ) : (
                <span className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/40 font-bold">● Sospeso</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-1 shrink-0">
          <button onClick={onEdit} className="p-2 border border-[#0A0A0A]/20 hover:border-[#FF5A1F] hover:text-[#FF5A1F]" data-testid={`user-edit-${user.id}`}>
            <Pencil size={14} />
          </button>
          <button onClick={onRemove} className="p-2 border border-[#0A0A0A]/20 hover:border-red-600 hover:text-red-600" data-testid={`user-delete-${user.id}`}>
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Contacts */}
      <div className="text-sm space-y-1.5">
        <div className="flex items-center gap-2 text-[#0A0A0A]/80 truncate">
          <Mail size={13} className="text-[#0A0A0A]/40 shrink-0" />
          <span className="truncate">{user.email}</span>
        </div>
        {user.phone && (
          <div className="flex items-center gap-2 text-[#0A0A0A]/80">
            <Phone size={13} className="text-[#0A0A0A]/40 shrink-0" /> {user.phone}
          </div>
        )}
      </div>

      {/* Seller‑specific block */}
      {isSeller && (
        <div className="border-t border-[#0A0A0A]/10 pt-3 grid grid-cols-2 gap-3 text-xs">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/50 font-bold flex items-center gap-1">
              <TrendingUp size={11} /> Commissione
            </div>
            <div className="mt-1 font-display text-2xl text-[#FF5A1F] leading-none">{user.commission_pct || 0}%</div>
          </div>
          {user.override_pct > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/50 font-bold flex items-center gap-1">
                <Crown size={11} /> Override
              </div>
              <div className="mt-1 font-display text-2xl text-[#0A0A0A] leading-none">{user.override_pct || 0}%</div>
            </div>
          )}
          {parentName && (
            <div className="col-span-2">
              <div className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/50 font-bold flex items-center gap-1">
                <Users2 size={11} /> Referente
              </div>
              <div className="mt-1 text-sm font-bold">{parentName}</div>
            </div>
          )}
        </div>
      )}

      {/* Artist-specific block */}
      {isArtist && (
        <div className="border-t border-[#0A0A0A]/10 pt-3 text-xs">
          <div className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/50 font-bold">Collegato al roster</div>
          <div className="mt-1 text-sm font-bold">
            {linkedArtistName ? linkedArtistName : <span className="text-amber-700">⚠ Non collegato al roster pubblico</span>}
          </div>
        </div>
      )}
    </div>
  );
};

const EditUserModal = ({ data, sellers, artists, onChange, onClose, onSave }) => {
  const set = (k, v) => onChange({ ...data, [k]: v });
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#FAFAF7] max-w-lg w-full max-h-[92vh] overflow-y-auto border-2 border-[#0A0A0A]" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#0A0A0A] text-[#FAFAF7] p-4 flex justify-between z-10">
          <h3 className="font-display text-2xl uppercase">{data.id ? "Modifica" : "Nuovo"} utente</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-6 space-y-3">
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Ruolo</span>
            <select value={data.role} onChange={(e) => set("role", e.target.value)} className="mt-1 block w-full border-2 border-[#0A0A0A] py-2 px-3 bg-white text-sm" data-testid="user-edit-role">
              {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          <Field label="Nome completo" value={data.name} onChange={(v) => set("name", v)} testId="user-edit-name" />
          <Field label="Email" type="email" value={data.email} onChange={(v) => set("email", v)} disabled={!!data.id} testId="user-edit-email" />
          {!data.id && (
            <Field label="Password (min 8 caratteri)" type="password" value={data.password} onChange={(v) => set("password", v)} testId="user-edit-password" />
          )}
          <Field label="Telefono" value={data.phone} onChange={(v) => set("phone", v)} testId="user-edit-phone" />
          {data.role === "seller" && (
            <div className="border-2 border-[#FF5A1F]/30 p-4 bg-[#FF5A1F]/5 space-y-3">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#FF5A1F]">Commissioni</div>
              <Field label="% Commissione base sulle sue vendite" type="number" value={data.commission_pct} onChange={(v) => set("commission_pct", v)} testId="user-edit-commission" />
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Referente (senior seller)</span>
                <select value={data.parent_seller_id || ""} onChange={(e) => set("parent_seller_id", e.target.value)} className="mt-1 block w-full border-2 border-[#0A0A0A] py-2 px-3 bg-white text-sm" data-testid="user-edit-parent">
                  <option value="">— Nessuno —</option>
                  {sellers.filter((s) => s.id !== data.id).map((s) => <option key={s.id} value={s.id}>{s.name || s.email}</option>)}
                </select>
              </label>
              <Field label="% Override sui sub‑seller (se è referente)" type="number" value={data.override_pct} onChange={(v) => set("override_pct", v)} testId="user-edit-override" />
              <div className="text-[10px] text-[#0A0A0A]/60 leading-relaxed">
                Esempio: Se un sub‑seller vende €1.000 con 15% di commissione base, prende €150. Se ha un referente con 5% override, il referente prende in più €50 su quella vendita.
              </div>
            </div>
          )}
          {data.role === "artist" && (
            <div className="border-2 border-[#FF5A1F]/30 p-4 bg-[#FF5A1F]/5 space-y-3">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#FF5A1F]">Profilo Artista</div>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Collega al roster pubblico</span>
                <select value={data.linked_artist_id || ""} onChange={(e) => set("linked_artist_id", e.target.value)} className="mt-1 block w-full border-2 border-[#0A0A0A] py-2 px-3 bg-white text-sm" data-testid="user-edit-linked-artist">
                  <option value="">— Nessun artista collegato —</option>
                  {(artists || []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </label>
              <div className="text-[10px] text-[#0A0A0A]/60 leading-relaxed">
                L'artista vede solo i propri contenuti in <code className="bg-white/60 px-1">/crm</code>. Se non colleghi un profilo del roster, l'artista non vedrà materiali né contratti.
              </div>
            </div>
          )}
          <label className="flex items-center gap-2 pt-2">
            <input type="checkbox" checked={data.active !== false} onChange={(e) => set("active", e.target.checked)} data-testid="user-edit-active" />
            <span className="text-sm">Utente attivo (può accedere)</span>
          </label>
        </div>
        <div className="sticky bottom-0 bg-[#FAFAF7] border-t-2 border-[#0A0A0A] p-4 flex justify-end gap-3">
          <button onClick={onClose} className="btn-outline-dark" data-testid="user-cancel">Annulla</button>
          <button onClick={onSave} className="btn-cta" data-testid="user-save"><Save size={14} /> Salva</button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, type = "text", testId, disabled }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">{label}</span>
    <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} data-testid={testId}
      className="mt-1 block w-full border-2 border-[#0A0A0A] py-2 px-3 bg-white text-sm disabled:opacity-60" />
  </label>
);
